import { useState, useEffect, useRef } from "react";

const WS_URL = process.env.REACT_APP_WS_URL || "ws://localhost:3001";

export default function App() {
  const [messages, setMessages] = useState([]);
  const [status, setStatus] = useState("idle");
  const [txHash, setTxHash] = useState(null);
  const [darkMode, setDarkMode] = useState(true);
  const wsRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const theme = {
    bg: darkMode ? "#0a0f0a" : "#f0f4f0",
    card: darkMode ? "#111811" : "#ffffff",
    card2: darkMode ? "#0d150d" : "#f8fdf8",
    border: darkMode ? "#1a2e1a" : "#c8e6c8",
    text: darkMode ? "#e2f0e2" : "#0a1a0a",
    sub: darkMode ? "#4a7a4a" : "#2d5a2d",
    muted: darkMode ? "#2a4a2a" : "#8aaa8a",
    green: "#00d084",
    darkGreen: "#00a866",
    buyerBg: darkMode ? "#0d1f0d" : "#e8f5e8",
    sellerBg: darkMode ? "#0f1a0f" : "#f0f8f0",
    dealBg: darkMode ? "#0a2a1a" : "#d4f5e4",
    errorBg: darkMode ? "#2a0a0a" : "#fde8e8",
    swapBg: darkMode ? "#1a1a0a" : "#fefde8",
  };

  function connect(cb) {
    const ws = new WebSocket(WS_URL);
    wsRef.current = ws;
    ws.onopen = () => cb(ws);
    ws.onmessage = (e) => {
      const data = JSON.parse(e.data);
      setMessages((prev) => [...prev, data]);
      if (data.type === "SWAP_CONFIRMED") { setTxHash(data.txHash); setStatus("confirmed"); }
      if (data.type === "NO_DEAL") setStatus("nodeal");
      if (data.type === "SWAP_ERROR") setStatus("swaperror");
      if (data.type === "EXECUTING_SWAP") setStatus("swapping");
      if (data.type === "DEAL") setStatus("deal");
    };
    ws.onclose = () => setStatus("idle");
  }

  function startNegotiation() {
    setMessages([]);
    setTxHash(null);
    setStatus("negotiating");
    connect((ws) => ws.send(JSON.stringify({ type: "START_NEGOTIATION" })));
  }

  function getBubbleStyle(type) {
    if (type === "BUYER") return { background: theme.buyerBg, borderLeft: "3px solid " + theme.green };
    if (type === "SELLER") return { background: theme.sellerBg, borderLeft: "3px solid #00b894" };
    if (type === "DEAL") return { background: theme.dealBg, borderLeft: "3px solid " + theme.green };
    if (type === "SWAP_CONFIRMED") return { background: theme.dealBg, borderLeft: "3px solid " + theme.green };
    if (type === "SWAP_ERROR") return { background: theme.errorBg, borderLeft: "3px solid #e74c3c" };
    if (type === "EXECUTING_SWAP") return { background: theme.swapBg, borderLeft: "3px solid #f1c40f" };
    if (type === "SWAP_SUBMITTED") return { background: theme.swapBg, borderLeft: "3px solid #f39c12" };
    return { background: theme.card2, borderLeft: "3px solid " + theme.muted };
  }

  function getLabel(type) {
    if (type === "BUYER") return "Buyer Agent";
    if (type === "SELLER") return "Seller Agent";
    if (type === "DEAL") return "Deal Reached";
    if (type === "ROUND") return "Round";
    if (type === "START") return "NegotiX";
    if (type === "EXECUTING_SWAP") return "Executing Swap";
    if (type === "SWAP_SUBMITTED") return "Submitted";
    if (type === "SWAP_CONFIRMED") return "Confirmed Onchain";
    if (type === "SWAP_ERROR") return "Error";
    if (type === "NO_DEAL") return "No Deal";
    return type;
  }

  const busy = status === "negotiating" || status === "swapping";

  return (
    <div style={{ minHeight: "100vh", background: theme.bg, color: theme.text, fontFamily: "monospace", transition: "all 0.3s" }}>
      <div style={{ maxWidth: 820, margin: "0 auto", padding: "24px 16px" }}>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 32 }}>
          <div>
            <h1 style={{ fontSize: 32, fontWeight: 900, color: theme.green, margin: 0, letterSpacing: 2 }}>NegotiX</h1>
            <p style={{ color: theme.sub, marginTop: 4, fontSize: 13 }}>Autonomous Agent-to-Agent Token Negotiation on X Layer</p>
          </div>
          <button
            onClick={() => setDarkMode(!darkMode)}
            style={{ background: theme.card, border: "1px solid " + theme.border, borderRadius: 20, padding: "8px 16px", color: theme.text, cursor: "pointer", fontFamily: "monospace", fontSize: 12 }}
          >
            {darkMode ? "Light Mode" : "Dark Mode"}
          </button>
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
          {["X Layer Mainnet", "OKX OnchainOS", "Uniswap Skills", "Chain ID: 196"].map((tag, i) => (
            <span key={i} style={{ background: theme.card, border: "1px solid " + theme.border, padding: "4px 12px", borderRadius: 20, fontSize: 11, color: i === 0 ? theme.green : theme.sub }}>
              {tag}
            </span>
          ))}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 24 }}>
          <div style={{ background: theme.card, borderRadius: 12, padding: 16, border: "1px solid " + theme.border, borderTop: "2px solid " + theme.green }}>
            <div style={{ fontSize: 10, color: theme.muted, marginBottom: 6, letterSpacing: 1 }}>BUYER AGENT</div>
            <div style={{ fontSize: 11, color: theme.green, marginBottom: 4 }}>0x5F3f...306e</div>
            <div style={{ fontSize: 11, color: theme.sub }}>Strategy: Bid low, converge up</div>
            <div style={{ marginTop: 8, height: 2, background: "linear-gradient(90deg," + theme.green + ",transparent)" }} />
          </div>
          <div style={{ background: theme.card, borderRadius: 12, padding: 16, border: "1px solid " + theme.border, borderTop: "2px solid #00b894" }}>
            <div style={{ fontSize: 10, color: theme.muted, marginBottom: 6, letterSpacing: 1 }}>SELLER AGENT</div>
            <div style={{ fontSize: 11, color: "#00b894", marginBottom: 4 }}>0xF2D4...8e6</div>
            <div style={{ fontSize: 11, color: theme.sub }}>Strategy: Ask high, converge down</div>
            <div style={{ marginTop: 8, height: 2, background: "linear-gradient(90deg,#00b894,transparent)" }} />
          </div>
        </div>

        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <button
            onClick={startNegotiation}
            disabled={busy}
            style={{
              background: busy ? theme.card : "linear-gradient(90deg," + theme.green + "," + theme.darkGreen + ")",
              color: busy ? theme.muted : "#000",
              border: busy ? "1px solid " + theme.border : "none",
              borderRadius: 12,
              padding: "14px 48px",
              fontSize: 15,
              fontWeight: 900,
              cursor: busy ? "not-allowed" : "pointer",
              fontFamily: "monospace",
              letterSpacing: 1,
              transition: "all 0.2s"
            }}
          >
            {status === "negotiating" ? "Negotiating..." : status === "swapping" ? "Executing Swap..." : "Start Negotiation"}
          </button>
        </div>

        {status === "confirmed" && txHash && (
          <div style={{ background: theme.dealBg, border: "1px solid " + theme.green, borderRadius: 12, padding: 16, marginBottom: 24, textAlign: "center" }}>
            <div style={{ color: theme.green, fontWeight: 700, marginBottom: 8, fontSize: 14 }}>Swap Executed On X Layer Mainnet</div>
            <a href={"https://www.okx.com/explorer/x-layer/tx/" + txHash} target="_blank" rel="noreferrer" style={{ color: theme.green, fontSize: 12, wordBreak: "break-all" }}>
              View on X Layer Explorer
            </a>
          </div>
        )}

        <div style={{ background: theme.card, borderRadius: 12, padding: 16, minHeight: 320, maxHeight: 480, overflowY: "auto", border: "1px solid " + theme.border }}>
          {messages.length === 0 && (
            <div style={{ textAlign: "center", color: theme.muted, paddingTop: 100, fontSize: 13 }}>
              Press Start Negotiation to watch agents negotiate a live OKB/USDT deal on X Layer
            </div>
          )}
          {messages.map((msg, i) => (
            <div key={i} style={{ ...getBubbleStyle(msg.type), borderRadius: 8, padding: "10px 14px", marginBottom: 10 }}>
              <div style={{ fontSize: 10, color: theme.muted, marginBottom: 4 }}>
                {getLabel(msg.type)}{msg.timestamp ? " · " + new Date(msg.timestamp).toLocaleTimeString() : ""}
              </div>
              <div style={{ fontSize: 13, color: theme.text }}>{msg.message}</div>
              {msg.offer && (
                <div style={{ fontSize: 12, color: theme.sub, marginTop: 4 }}>
                  Offer: ${msg.offer} · Market: ${msg.marketPrice}
                </div>
              )}
              {msg.txHash && (
                <a href={"https://www.okx.com/explorer/x-layer/tx/" + msg.txHash} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: theme.green }}>
                  {msg.txHash.slice(0, 24)}... View on Explorer
                </a>
              )}
            </div>
          ))}
          <div ref={bottomRef} />
        </div>

        <div style={{ textAlign: "center", marginTop: 24, fontSize: 11, color: theme.muted }}>
          Built for OKX Build X Hackathon · X Layer Mainnet · Chain ID 196
        </div>
        <div style={{ textAlign: "center", marginTop: 8, fontSize: 11 }}>
          Built by{" "}
          <a href="https://x.com/dingidingi5" target="_blank" rel="noreferrer" style={{ color: theme.green, textDecoration: "none", fontWeight: 700 }}>
            dingidingi
          </a>
        </div>

      </div>
    </div>
  );
}
